import { useState, useEffect, useRef } from "react"
import { useTheme } from "@mui/material/styles"
import { AxiosRequestConfig } from "axios"
import { useQuery } from "@tanstack/react-query"
import {fetchWithToken} from "api/data"
import { default as routes} from "api/routes"
import {
  ReactFlow,
  useNodesState,
  useEdgesState,
  Edge,
  Node,
  Background,
  Controls,
  ColorMode,
  Position,
} from "@xyflow/react"
import { IconDisplay } from "components/ui/icons"
import { Fullscreen } from "components/ui/buttons"
import { DateRangeSlider} from "components/ui/forms"
import { PipeEdge, ObjectNode, GetEdgeStyle } from "components/flow"
import "@xyflow/react/dist/style.css"
import { DisplayValue } from "components/ui/utils"
import { Title } from "react-admin"
import { useTranslate } from "ra-core"
import { AppBar, Box, Tabs } from "@mui/material"
import { TabWithIcon } from "components/ui/layouts"

const edgeTypes = {
  pipe: PipeEdge,
}
const nodeDefaults = {
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    sensorData: undefined,
}
const nodeTypes = {
    objectNode: ObjectNode,
}

const TabList = ({onSelectTab}: {onSelectTab(id: number): void}) => {
  const translate = useTranslate()
  const [activeTab, setActiveTab] = useState<number>(0) 

  const selectTab = (id: number) => {
    setActiveTab(id)
    onSelectTab(id)
  }

  return (
      <Box display="flex" flexDirection="column" width="100%">
         <AppBar position="static" color="default">
                <Tabs
                    value={activeTab}
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <TabWithIcon label={translate("internal.current_data")} onClick={() => selectTab(0)} />
                    <TabWithIcon label={translate("internal.historical_data")} onClick={() => selectTab(1)} /*onClick={handleCombinedTabClick}  onClick={() => navigate(`/${tab?.resource}/${recordId}/${tab?.name}`)} icon={} *//>
                </Tabs>
            </AppBar>
      </Box>
  )
}

function ObjectFlow(props: {group: string, kind: string}): JSX.Element  {
    const { group, kind } = props
    const theme = useTheme()
    const translate = useTranslate()
    const appColorMode = theme.palette.mode 
    const [colorMode, setColorMode] = useState<ColorMode>(appColorMode)
    const [activetab, setActivetab] = useState<number>(0)
    const [initialStartDate, setInitialStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 3)))
    const [initialEndDate, setInitialEndDate] = useState(new Date())
    const [initialCurrentDate, setInitialCurrentDate] = useState(new Date())
    const [nodes, setNodes, onNodesChange] = useNodesState([])
    const [edges, setEdges, onEdgesChange] = useEdgesState([])
    const [objects, setObjects] = useState<string[]>([])
    const flowRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        setColorMode(appColorMode)
    }, [appColorMode])

    useEffect(() => {
        const config: AxiosRequestConfig = {
            url: routes.objectFlow(group),
            method: 'get',
        }
        fetchWithToken(config)
          .then((response) => {
            const updatedNodes = response.data?.nodes.map((node: Node) => ({
              ...node,
              ...{data: {icon: <IconDisplay iconType={node?.type || ""} />, type: node?.type, ...node.data, ...nodeDefaults}},
              ...{type: "objectNode"}
            }))
            
            const updatedEdges = response.data?.edges.map((edge: Edge & {target_type: string}) => ({
                ...edge,
                style: GetEdgeStyle(edge.target_type),
                //markerEnd: {
                //  type: MarkerType.ArrowClosed,
                //},
                //animated: false,
            }))
            setNodes(updatedNodes)
            setEdges(updatedEdges)
            setObjects(updatedNodes?.map((node: Node) => node.id) || [])
          })
          .then(() => {
            return Promise.resolve()
          })
          .catch((error) => Promise.reject(error))
    }, [])

    const { data: sensorDataRaw, refetch } = useQuery({
      queryKey: ['flow_sensordata', group],
      queryFn: () => {
          if (!nodes || nodes.length === 0) {
            return Promise.resolve([])
          }

          const config: AxiosRequestConfig = {
              url: routes.deviceDataFlow(kind, (activetab === 0), initialCurrentDate),
              method: 'get',
              params: { "ids":  objects},
          }
          return fetchWithToken(config).then(response => response.data)
      },
      refetchInterval: 60000, //TODO
    })

    useEffect(() => {
      if (objects && objects.length > 0) {
          refetch()
      }
    }, [objects, refetch])

    useEffect(() => {
      if (activetab === 1) {
        setInitialStartDate(new Date(new Date().setDate(new Date().getDate() - 3)))
        setInitialEndDate(new Date())
        setInitialCurrentDate(new Date())
      }
    }, [activetab, refetch])

    const handleDateChange = (date: Date) => {
      setInitialCurrentDate(date)
    }
  
    useEffect(() => {
      if (activetab === 1) {
        refetch()
      }
    }, [activetab, refetch, initialCurrentDate])
  
    useEffect(() => {
        if (sensorDataRaw && sensorDataRaw.length > 0) {
          setNodes((prevNodes: any) =>
            prevNodes.map((node: any) => {
                const sensorData: any | undefined = sensorDataRaw.find((s: any) => s?.object_id === node.id)
                return {
                    ...node,
                    data: {
                        ...node.data,
                        sensorData: DisplayValue(kind, sensorData),
                        deviceId: sensorData?.device_id,
                        deviceType: sensorData?.device_type,
                    },
                }
            })
          )
        }
    }, [sensorDataRaw, setNodes, kind])

    const toggleFullScreen = () => {
      if (!document.fullscreenElement) {
        flowRef.current?.requestFullscreen()
      } else {
        document.exitFullscreen()
      }
    }

    return (
      <>        
        <TabList onSelectTab={(tabid) => setActivetab(tabid)} />
        <div style={{display: activetab === 1 ? "block" : "none"}} >
          <DateRangeSlider 
            onChange={handleDateChange} 
            initialStartDate={initialStartDate}
            initialEndDate={initialEndDate}
            initialCurrentDate={initialCurrentDate} />
        </div>
        <div ref={flowRef} style={{ width: '100%', height:  activetab === 1 ? '85%' : '93%' }}>
          
          <Title title={translate('resources.objects.scheme_gts')}/>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodesDraggable={false}
            edgesReconnectable={false}
            edgesFocusable={false}
            nodesConnectable={false}
            onlyRenderVisibleElements={false}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            colorMode={colorMode}
            proOptions={{ hideAttribution: true }}
            defaultEdgeOptions={{ type: 'pipe', animated: true, }}
            fitView
          >
            {/*<MiniMap />*/}
            <Background />
            <Controls showInteractive={false} children={<Fullscreen onClick={toggleFullScreen} colorMode={appColorMode as string} />}/>

          </ReactFlow>
        </div>
      </>
    )
}

export default ObjectFlow


ObjectFlow.defaultProps = {
    kind: "ddt", //TODO
}